import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import {
  buildInvestigateEvidencePath,
  type OverviewConditionRow,
  type OverviewDecisionSummary,
  type OverviewPeriodConditionRow,
} from '../domain/continuousSafety/overviewSiteConditionSummary';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { formatSiteDateTime, msUntilNextOpenSitePt15mBoundary } from '../domain/siteTime/siteTime';
import { useOverviewCameraEvidence, type OverviewCameraEvidenceState } from '../hooks/useOverviewCameraEvidence';
import { useOverviewConditionEvidenceThumbs } from '../hooks/useOverviewConditionEvidenceThumbs';
import { collectOverviewEvidenceThumbRequests } from '../domain/continuousSafety/overviewConditionEvidenceThumb';
import { isCameraOnlineForEvidence, isUsableThumbnailSrc } from '../domain/overview/overviewCameraEvidence';
import { buildLiveSafetyCasePath } from '../utils/acceptanceHierarchyNavigation';
import type { Camera } from '../services/camerasApi';
import type { Zone } from '../services/zonesApi';
import { findZoneForEvidenceOverlay } from '../domain/liveView/overlayEvidenceSnapshot';
import { zoneOverlayLabel, zoneOverlayPolygon } from '../domain/liveView/zoneOverlayGeometry';
import {
  OverviewAreaCameraPanel,
  type OverviewAreaCameraPanelModel,
  type OverviewAreaGeometry,
} from './OverviewAreaCameraPanel';
import { CameraMeasurementPanel } from '../components/cameraMeasurement/CameraMeasurementPanel';
import type { CameraActivityMeasurementResponse } from '../services/cameraActivityMeasurementApi';
import { getEffectiveMeasurementDefinition } from '../services/effectiveMeasurementDefinitionApi';
import {
  composeOverviewDeployedCameraOverlays,
  overlayFromProvenPolygon,
} from './overviewV2DeployedGeometry';
import {
  buildOverviewV2SummaryTiles,
  composeZoneSafetyStory,
  controlPolicyStage,
  coverageIncompleteFromRow,
  currentPolicyStage,
  currentActivityStage,
  currentActivityTimeline,
  recurringChartExpandedAxisLabel,
  recurringOccurrenceChart,
  resolveOverviewV2FinalizedConditionMeasuredThrough,
  type OverviewV2RecurringChart,
  customerCameraLabel,
  customerZoneLabel,
  groupOverviewV2AreasBySpatialScope,
  resolveOverviewV2CameraPlacement,
  mergeOverviewV2AreaExpansionState,
  overviewV2AreaCollapsedSummary,
  overviewV2AreaGroupCounts,
  overviewV2AreaGroupKey,
  overviewV2AreaNeedsAttention,
  overviewV2CameraCardKey,
  overviewV2VisibleMountLabel,
  readOverviewV2AreaExpansionState,
  writeOverviewV2AreaExpansionState,
  type OverviewV2CameraPlacement,
  overviewV2PublicZoneId,
  buildOverviewV2CameraHealthFacts,
  formatOverviewV2CameraHealthRow,
  formatOverviewV2CameraFreshness,
  overviewV2AttentionOwnsZoneReview,
  overviewV2CameraHealthValue,
  overviewV2OnlineOfflineDetail,
  overviewV2ConditionSpatialScope,
  overviewV2ControlFollowUpDetail,
  overviewV2MeasurementScopeLabel,
  overviewV2NextActionWorkflowKey,
  overviewV2SiteAttentionBanner,
  recurringPolicyStage,
  recurringReviewCount,
  resolveOverviewV2PendingReviewCount,
  resolveOverviewV2ZonePrimaryAction,
  selectOverviewV2AttentionItems,
  type OverviewV2AttentionItem,
  type OverviewV2CameraHealthRow,
  type OverviewV2Stage,
} from './overviewV2Presentation';
import {
  OVERVIEW_V2_ROOM_B_NEAR_MISS_ID,
  buildOverviewV2PatternKey,
  resolveOverviewV2ControlName,
  type OverviewV2Binding,
  type OverviewV2WorkflowIdentity,
} from './overviewV2Binding';
import type { OverTimeWindowId } from '../domain/sellableSiteExperience/overTimePresenter';
import { useSiteLocationLookups } from '../hooks/useSiteLocationLookups';
import {
  OVERVIEW_V2_CURRENT_WINDOW_HEADING,
  OVERVIEW_V2_PREVIOUS_WINDOW_HEADING,
  overviewV2BannerActionItems,
  overviewV2ConditionDisplayName,
  overviewV2ConditionSubtitle,
  overviewV2CurrentActivityCount,
  overviewV2CurrentActivitySummaryDetail,
} from './overviewV2CustomerCopy';
import { ConditionLifecycleSpine } from '../components/conditionLifecycle/ConditionLifecycleSpine';
import { buildLifecycleScanVerifyPath } from '../domain/continuousSafety/conditionLifecycleNavigation';
import { SHOWCASE_BOOTH_SHORTCUT_HREF } from '../utils/warehouseNavigation';

const OVERVIEW_V2_RECURRING_WINDOWS: Array<{ id: OverTimeWindowId; label: string }> = [
  { id: 'rolling_24h', label: '1D' },
  { id: 'rolling_7d', label: '7D' },
  { id: 'rolling_30d', label: '30D' },
];

const colors = {
  ink: '#0f172a', ink2: '#4a5768', ink3: '#7d8798',
  line: '#e3e8ef', bg: '#f4f6f9', blue: '#1d4ed8', blueSoft: '#eef3ff',
  teal: '#1b7a86', amber: '#b45f0c', amberSoft: '#fff6ea', green: '#12744a', greenSoft: '#eef9f2',
  red: '#b42318', redSoft: '#fff7f6', greySoft: '#f1f4f8',
};

const zoneMatrixColumns = { xs: '1fr', md: '1.45fr 1fr 1fr 1fr 128px' };
const EMPTY_CAMERA_PLACEMENTS: Record<string, OverviewV2CameraPlacement> = {};

/** Re-render Current Activity when the site-local PT15M wall-clock window rolls. */
function useOpenSitePt15mClock(timeZone?: string | null): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timeoutId = 0;
    let cancelled = false;
    const schedule = () => {
      const delay = msUntilNextOpenSitePt15mBoundary(new Date(), timeZone);
      const waitMs = delay == null ? 15_000 : Math.max(250, delay);
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setNow(new Date());
        schedule();
      }, waitMs);
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [timeZone]);
  return now;
}

type V2NextOperatorAction = 'verify_observations' | 'record_control' | 'none';

type OverviewPolicy = {
  conditionId: string;
  label: string;
  subtitle: string;
  icon: string;
  current: OverviewConditionRow | null;
  recurring: OverviewPeriodConditionRow | null;
  isCanonical: boolean;
  priority: boolean;
};

export type MonitoredAreaViewModel = {
  key: string;
  siteId: string;
  canonicalScopeKey: string;
  location: string;
  title: string;
  cameraLabel: string;
  cameraId: string | null;
  deviceId: string | null;
  /** First condition's zone — overlay/metadata only, not camera-card identity. */
  zoneId: string | null;
  areaId: string | null;
  areaName: string | null;
  mountLabel: string | null;
  policies: OverviewPolicy[];
};

type AreaRoutes = {
  evidencePath: string | null;
  casePath: string | null;
  recordPath: string | null;
  zonePath: string | null;
  createCasePath: string;
  scanVerifyPath: string | null;
  recordedControl?: OverviewV2WorkflowIdentity['recordedControl'];
};

function text(value: unknown): string { return String(value || '').trim(); }

function formatMeasuredThrough(value: unknown, timeZone?: string | null): string {
  const raw = text(value);
  if (!raw) return 'Unavailable';
  return formatSiteDateTime(raw, timeZone) || raw;
}

function formatAreaLocation(value: string): string { return value.replace(/\s+-\s+/g, ' · '); }
function areaSortKey(location: string): string { return /room b/i.test(location) ? '0' : /room a/i.test(location) ? '1' : location; }

function policyIcon(label: string, subjectKind?: string | null): string {
  if (subjectKind === 'COMPOSITE') return 'CX';
  const normalized = label.toLowerCase();
  if (normalized.includes('forklift-pedestrian') || normalized.includes('near')) return 'NM';
  if (
    normalized.includes('restricted-area')
    || normalized.includes('restricted-zone')
    || normalized.includes('zone intrusion')
    || normalized.includes('zone violation')
  ) return 'ZI';
  if (normalized.includes('dwell')) return 'PD';
  if (normalized.includes('ppe')) return 'PP';
  if (normalized.includes('access')) return 'RA';
  return label.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'P';
}

function policySubtitle(row: OverviewConditionRow | OverviewPeriodConditionRow | null): string {
  return overviewV2ConditionSubtitle(row?.label, row?.incidentType, row?.conditionFamily) || '';
}

function isCompositePolicy(policy: OverviewPolicy): boolean {
  return policy.current?.subjectKind === 'COMPOSITE' || policy.recurring?.subjectKind === 'COMPOSITE';
}

function policyName(row: OverviewConditionRow | OverviewPeriodConditionRow | null): string {
  if (row && 'subjectKind' in (row as OverviewConditionRow) && (row as OverviewConditionRow).subjectKind === 'COMPOSITE') {
    return text(row.label);
  }
  if (row && 'subjectKind' in (row as OverviewPeriodConditionRow) && (row as OverviewPeriodConditionRow).subjectKind === 'COMPOSITE') {
    return text(row.label);
  }
  return overviewV2ConditionDisplayName(row?.label, row?.incidentType);
}

function areaCameraLabel(area: MonitoredAreaViewModel, camera?: Camera | null): string {
  return customerCameraLabel({ cameraName: camera?.name, location: area.location });
}

function areaCanonicalScopeKey(args: {
  siteId: string;
  cameraId: string | null;
  deviceId: string | null;
  conditionId?: string | null;
}): string {
  return overviewV2CameraCardKey(args);
}

function policyZoneId(area: MonitoredAreaViewModel, policy: OverviewPolicy): string | null {
  return overviewV2PublicZoneId(policy.current?.zoneId || policy.recurring?.zoneId || area.zoneId);
}

function buildAreas(
  summary: OverviewDecisionSummary | null,
  placements?: Record<string, OverviewV2CameraPlacement>,
  binding: OverviewV2Binding = 'live',
): MonitoredAreaViewModel[] {
  if (!summary) return [];
  const currentRows = summary.now.conditionRows || [];
  const recurringRows = summary.overTime.conditionRows || [];
  const recurringByCondition = new Map(recurringRows.map((row) => [row.conditionId, row]));
  const currentByCondition = new Map(currentRows.map((row) => [row.conditionId, row]));
  const conditionIds = new Set([...currentRows.map((row) => row.conditionId), ...recurringRows.map((row) => row.conditionId)]);
  const byArea = new Map<string, MonitoredAreaViewModel>();

  conditionIds.forEach((conditionId) => {
    const current = currentByCondition.get(conditionId) || null;
    const recurring = recurringByCondition.get(conditionId) || null;
    const row = current || recurring;
    if (!row) return;
    const isCanonical = binding === 'fixture' && conditionId === OVERVIEW_V2_ROOM_B_NEAR_MISS_ID;
    const location = text(row.location) || text(summary.sinceControl.controlLocation) || 'Monitored area';
    const zoneId = overviewV2PublicZoneId(row.zoneId);
    const cameraId = text(row.cameraId) || null;
    const deviceId = text(row.deviceId) || null;
    const placement = resolveOverviewV2CameraPlacement({ cameraId, deviceId }, placements);
    const canonicalScopeKey = areaCanonicalScopeKey({
      siteId: summary.siteId,
      cameraId,
      deviceId,
      conditionId,
    });
    const areaKey = canonicalScopeKey;
    if (!byArea.has(areaKey)) {
      byArea.set(areaKey, {
        key: areaKey,
        siteId: summary.siteId,
        canonicalScopeKey,
        location,
        title: formatAreaLocation(location),
        cameraLabel: cameraId || deviceId || 'Area camera',
        cameraId,
        deviceId,
        zoneId,
        areaId: text(placement?.areaId) || null,
        areaName: text(placement?.areaName) || null,
        mountLabel: text(placement?.mountLabel) || null,
        policies: [],
      });
    }
    byArea.get(areaKey)?.policies.push({
      conditionId, label: policyName(row), subtitle: policySubtitle(row), icon: policyIcon(policyName(row), current?.subjectKind || recurring?.subjectKind), current, recurring, isCanonical,
      priority: Boolean(current?.attentionRequired),
    });
  });

  return Array.from(byArea.values())
    .map((area) => ({
      ...area,
      policies: area.policies.sort((a, b) => Number(b.isCanonical || b.priority) - Number(a.isCanonical || a.priority)),
    }))
    .sort((a, b) => areaSortKey(a.location).localeCompare(areaSortKey(b.location)));
}

export function selectOverviewV2RepresentativeAreas(summary: OverviewDecisionSummary | null): MonitoredAreaViewModel[] { return buildAreas(summary); }
export function selectOverviewV2RepresentativeArea(summary: OverviewDecisionSummary | null): MonitoredAreaViewModel | null { return buildAreas(summary)[0] || null; }
export function sanitizeOverviewV2NextAction(value: unknown): V2NextOperatorAction {
  const action = text(value).toLowerCase();
  if (action === 'verify_observations' || action === 'record_control') return action;
  return 'none';
}

export function selectOverviewV2CameraForArea(cameras: Camera[], area: MonitoredAreaViewModel): Camera | null {
  if (area.cameraId) {
    const matches = cameras.filter((camera) => text(camera.camera_id) === area.cameraId);
    if (area.deviceId) {
      const exact = matches.find((camera) => text(camera.edge_device_id) === area.deviceId) || null;
      if (exact) return exact;
      return null;
    }
    if (matches.length === 1) return matches[0];
    return null;
  }
  if (area.deviceId) {
    const byDevice = cameras.filter((camera) => text(camera.edge_device_id) === area.deviceId);
    if (byDevice.length === 1) return byDevice[0];
  }
  return null;
}

export function resolveOverviewV2SnapshotUrl(
  thumbnails: Record<string, string>,
  area: Pick<MonitoredAreaViewModel, 'cameraId'> & { policies?: Array<{ conditionId: string }> },
  camera: Camera | null,
  evidenceThumbs?: Record<string, { imageUrl?: string | null; status?: string } | null>,
): string | null {
  if (area.cameraId && isUsableThumbnailSrc(thumbnails[area.cameraId])) return thumbnails[area.cameraId];
  if (camera?.camera_id && isUsableThumbnailSrc(thumbnails[camera.camera_id])) return thumbnails[camera.camera_id];
  for (const policy of area.policies || []) {
    const thumb = evidenceThumbs?.[policy.conditionId];
    if (thumb?.imageUrl && isUsableThumbnailSrc(thumb.imageUrl) && (!thumb.status || thumb.status === 'ready')) {
      return thumb.imageUrl;
    }
  }
  return null;
}

function findZone(zones: Zone[], area: MonitoredAreaViewModel): Zone | null {
  if (!area.zoneId) return null;
  return findZoneForEvidenceOverlay(zones, area.zoneId, area.cameraId || undefined);
}

function buildCreateSafetyCasePath(siteId: string): string {
  const query = new URLSearchParams();
  if (siteId) query.set('site_id', siteId);
  return `/acceptance/cases${query.toString() ? `?${query.toString()}` : ''}`;
}

function resolvePolicyWorkflow(
  area: MonitoredAreaViewModel,
  policy: OverviewPolicy,
  workflowIdentity?: OverviewV2WorkflowIdentity | null,
  workflowIdentities?: OverviewV2WorkflowIdentity[],
): OverviewV2WorkflowIdentity {
  const listed = (workflowIdentities || []).find((row) => row.conditionId === policy.conditionId);
  const single = workflowIdentity?.conditionId === policy.conditionId ? workflowIdentity : null;
  const row = policy.current || policy.recurring;
  return {
    conditionId: policy.conditionId,
    patternKey: listed?.patternKey || single?.patternKey || buildOverviewV2PatternKey({
      incidentType: row?.incidentType,
      zoneId: policyZoneId(area, policy) || row?.zoneId,
      cameraId: area.cameraId || row?.cameraId,
      deviceId: area.deviceId || row?.deviceId,
    }) || undefined,
    caseId: listed?.caseId || single?.caseId,
    ccrId: listed?.ccrId || single?.ccrId,
    recordedControl: listed?.recordedControl || single?.recordedControl || null,
  };
}

function routesForPolicy(
  siteId: string,
  area: MonitoredAreaViewModel,
  policy: OverviewPolicy,
  workflowIdentity?: OverviewV2WorkflowIdentity | null,
  workflowIdentities?: OverviewV2WorkflowIdentity[],
): AreaRoutes {
  if (isCompositePolicy(policy)) {
    const currentId = text(policy.current?.compositeIncidentId);
    const reviewPath = text(policy.recurring?.compositeReviewPath)
      || (text(policy.recurring?.compositePatternIncidentId)
        ? `/live?incident_id=${encodeURIComponent(text(policy.recurring?.compositePatternIncidentId))}`
        : '');
    const evidencePath = policy.current?.compositeEvidencePath
      || (currentId ? `/live?incident_id=${encodeURIComponent(currentId)}` : reviewPath || null);
    const controlPath = text(policy.recurring?.compositeControlPath) || null;
    const recordedId = text(
      policy.recurring?.lifecycle?.controlRevisionId
      || policy.recurring?.sinceControl?.controlId
      || policy.current?.sinceControl?.controlId,
    );
    return {
      evidencePath,
      casePath: reviewPath || null,
      recordPath: controlPath,
      zonePath: evidencePath,
      createCasePath: '',
      scanVerifyPath: controlPath
        ? controlPath.replace('focus=control', 'focus=scan')
        : null,
      recordedControl: recordedId
        ? {
          controlId: recordedId,
          title: 'Recorded control',
          recordedAt: policy.recurring?.lifecycle?.sinceStartedAt
            || policy.recurring?.sinceControl?.anchorStartedAt
            || null,
          statusLabel: policy.recurring?.sinceControl?.bucketCount
            ? `${policy.recurring.sinceControl.bucketCount} occurrences observed since the control was recorded.`
            : 'Linked through condition lifecycle',
        }
        : null,
    };
  }
  const workflow = resolvePolicyWorkflow(area, policy, workflowIdentity, workflowIdentities);
  const evidencePath = buildInvestigateEvidencePath({
    siteId, zoneId: policyZoneId(area, policy), cameraId: area.cameraId, deviceId: area.deviceId,
    incidentType: policy.current?.incidentType || policy.recurring?.incidentType || null,
    conditionId: policy.conditionId,
    conditionFamily: policy.current?.conditionFamily || policy.recurring?.conditionFamily || null,
    groupKey: workflow.patternKey,
    workflowId: workflow.patternKey,
    review: true,
  });
  const casePath = workflow.caseId
    ? buildLiveSafetyCasePath({ caseId: workflow.caseId, siteId, workflowId: workflow.patternKey })
    : null;
  const recordPath = workflow.ccrId ? `/acceptance/reports/records/${encodeURIComponent(workflow.ccrId)}` : null;
  const lifecycle = policy.recurring?.lifecycle || null;
  const scanReportId = lifecycle?.latestScan?.afterReportId || null;
  const scanVerifyPath = scanReportId
    ? buildLifecycleScanVerifyPath({
      siteId,
      conditionId: policy.conditionId,
      areaId: area.areaId,
      zoneId: policyZoneId(area, policy),
      cameraId: area.cameraId,
      reportId: scanReportId,
    })
    : null;
  return {
    evidencePath,
    casePath,
    recordPath,
    zonePath: buildZonePath(siteId, area, policyZoneId(area, policy)),
    createCasePath: buildCreateSafetyCasePath(siteId),
    scanVerifyPath,
    recordedControl: workflow.recordedControl || null,
  };
}

function buildCameraPath(siteId: string, area: MonitoredAreaViewModel): string | null {
  if (!area.cameraId) return null;
  const query = new URLSearchParams();
  if (siteId) query.set('site', siteId);
  if (area.deviceId) query.set('device_id', area.deviceId);
  return `/cameras/${encodeURIComponent(area.cameraId)}${query.toString() ? `?${query.toString()}` : ''}`;
}

function buildLiveViewPath(siteId: string, area: MonitoredAreaViewModel): string | null {
  if (!area.cameraId && !area.deviceId) return null;
  const query = new URLSearchParams();
  if (siteId) query.set('site', siteId);
  if (area.cameraId) query.set('camera_id', area.cameraId);
  if (area.deviceId) query.set('device_id', area.deviceId);
  return `/live-view${query.toString() ? `?${query.toString()}` : ''}`;
}

function buildZonePath(siteId: string, area: MonitoredAreaViewModel, zoneId?: string | null): string | null {
  const resolvedZoneId = overviewV2PublicZoneId(zoneId || area.zoneId);
  if (!resolvedZoneId) return null;
  const query = new URLSearchParams();
  if (siteId) query.set('site', siteId);
  query.set('zone_id', resolvedZoneId);
  if (area.cameraId) query.set('camera_id', area.cameraId);
  if (area.deviceId) query.set('device_id', area.deviceId);
  return `/zones?${query.toString()}`;
}

function useOverviewDeployedOverlays(
  siteId: string,
  areas: MonitoredAreaViewModel[],
  fixtureZones?: Zone[],
): Record<string, OverviewAreaGeometry[]> {
  const [resolved, setResolved] = useState<Record<string, OverviewAreaGeometry[]>>({});
  const areaKey = useMemo(
    () => areas.map((area) => `${area.key}:${area.policies.map((policy) => policy.conditionId).join(',')}`).join('|'),
    [areas],
  );

  useEffect(() => {
    if (fixtureZones) {
      const next: Record<string, OverviewAreaGeometry[]> = {};
      areas.forEach((area) => {
        const cameraZones = fixtureZones.filter((zone) => (
          !area.cameraId
          || !zone.camera_id
          || text(zone.camera_id) === area.cameraId
        ));
        const matched = cameraZones.length
          ? cameraZones
          : [findZone(fixtureZones, area)].filter(Boolean) as Zone[];
        next[area.key] = matched
          .map((zone) => overlayFromProvenPolygon({
            zoneId: text(zone.zone_id),
            label: zoneOverlayLabel(zone) || text(zone.zone_id),
            polygon: zoneOverlayPolygon(zone),
            sourceLabel: 'fixture provider geometry',
            measurementScopeKind: 'ZONE_POLYGON',
          }))
          .filter((item): item is OverviewAreaGeometry => Boolean(item));
      });
      setResolved(next);
      return undefined;
    }

    let cancelled = false;
    setResolved({});
    const requests = areas.flatMap((area) => area.policies.map((policy) => ({
      areaKey: area.key,
      cameraId: area.cameraId,
      deviceId: area.deviceId,
      conditionId: policy.conditionId,
    }))).filter((item) => item.conditionId);

    void Promise.all(requests.map(async (item) => {
      try {
        const result = await getEffectiveMeasurementDefinition({
          siteId,
          conditionId: item.conditionId,
        });
        return { ...item, payload: result.kind === 'ok' ? result.data : null };
      } catch {
        return { ...item, payload: null };
      }
    })).then((entries) => {
      if (cancelled) return;
      const next: Record<string, OverviewAreaGeometry[]> = {};
      areas.forEach((area) => {
        const payloads = entries
          .filter((entry) => entry.areaKey === area.key)
          .map((entry) => entry.payload);
        next[area.key] = composeOverviewDeployedCameraOverlays({
          payloads,
          cameraId: area.cameraId,
          deviceId: area.deviceId,
        });
      });
      setResolved(next);
    });

    return () => {
      cancelled = true;
    };
    // areaKey already encodes camera/policy identity; `areas` object identity churned overlay state.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- areas is represented by areaKey
  }, [areaKey, fixtureZones, siteId]);

  return resolved;
}

function Badge({
  children,
  tone = 'default',
  variant = 'soft',
  testId,
}: {
  children: React.ReactNode;
  tone?: 'default' | 'ok' | 'alert' | 'amber';
  variant?: 'soft' | 'outline' | 'filled';
  testId?: string;
}) {
  const palette = {
    default: { bg: colors.greySoft, fg: colors.ink2, border: colors.line },
    ok: { bg: colors.greenSoft, fg: colors.green, border: '#cfe8d8' },
    alert: { bg: '#fff0ee', fg: colors.red, border: '#f2c8c3' },
    amber: { bg: colors.amberSoft, fg: colors.amber, border: '#f0d2a8' },
  }[tone];
  const filled = variant === 'filled' && tone === 'alert';
  return (
    <Box
      component="span"
      data-testid={testId}
      data-chip-variant={variant}
      sx={{
        fontSize: 8.5,
        fontWeight: 700,
        borderRadius: 999,
        px: '7px',
        py: '3px',
        whiteSpace: 'nowrap',
        bgcolor: filled ? colors.red : variant === 'outline' ? '#fff' : palette.bg,
        color: filled ? '#fff' : palette.fg,
        border: `1px solid ${filled ? colors.red : palette.border}`,
      }}
    >
      {children}
    </Box>
  );
}

function OverviewLink({
  href,
  children,
  onNavigate,
  sx,
  testId,
  variant = 'link',
}: {
  href: string;
  children: React.ReactNode;
  onNavigate?: (path: string) => void;
  sx?: Record<string, unknown>;
  testId?: string;
  variant?: 'link' | 'primary';
}) {
  const primary = variant === 'primary';
  return (
    <Box
      component="a"
      href={href}
      data-testid={testId}
      data-action-variant={variant}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => { if (!onNavigate) return; event.preventDefault(); onNavigate(href); }}
      sx={{
        color: primary ? '#fff' : colors.blue,
        textDecoration: 'none',
        fontWeight: primary ? 750 : 650,
        ...(primary ? {
          fontSize: 10,
          bgcolor: colors.blue,
          border: `1px solid ${colors.blue}`,
          borderRadius: '6px',
          p: '6px 10px',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        } : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Metric({ label, value, detail, testId, tone }: { label: string; value: string | number; detail: string; testId?: string; tone?: 'default' | 'alert' | 'amber' }) {
  const border = tone === 'alert' ? '#f2c8c3' : colors.line;
  return (
    <Box data-testid={testId} sx={{ bgcolor: '#fff', border: `1px solid ${border}`, borderRadius: '9px', p: '9px 11px', minWidth: 0 }}>
      <Box sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.55px', textTransform: 'uppercase', color: tone === 'alert' ? colors.red : colors.ink3 }}>{label}</Box>
      <Box sx={{ fontSize: 18, fontWeight: 730, letterSpacing: '-.3px', mt: '1px' }}>{value}</Box>
      <Box sx={{ fontSize: 10, color: colors.ink2, mt: '1px' }}>{detail}</Box>
    </Box>
  );
}

function collectPolicyAttentionArgs(
  areas: MonitoredAreaViewModel[],
  siteId: string,
  workflowIdentity?: OverviewV2WorkflowIdentity | null,
  workflowIdentities?: OverviewV2WorkflowIdentity[],
) {
  return areas.flatMap((area) => area.policies.map((policy) => {
    const routes = routesForPolicy(siteId, area, policy, workflowIdentity, workflowIdentities);
    return {
      conditionId: policy.conditionId,
      label: policy.label,
      location: area.title,
      attentionRequired: policy.current?.attentionRequired,
      attentionReason: policy.current?.attentionReason,
      coverageIncomplete: coverageIncompleteFromRow(policy.current),
      coverageShortfallReason: policy.current?.coverageShortfallReason,
      coverageLabel: policy.current?.coverageLabel,
      currentThrough: policy.current?.currentThrough,
      cameraId: area.cameraId || policy.current?.cameraId,
      measurementValue: policy.current?.measurementValue,
      supportingActivityLabel: policy.current?.supportingActivityLabel,
      currentActivityCount: policy.current?.currentActivity?.occurrenceCountSoFar ?? null,
      evidencePath: routes.evidencePath,
      zonePath: routes.zonePath,
      recordPath: routes.recordPath,
      casePath: routes.casePath,
      scanVerifyPath: routes.scanVerifyPath,
      pendingReviewCount: resolveOverviewV2PendingReviewCount(policy.current),
      reviewSetId: policy.current?.reviewSetId || null,
    };
  }));
}

function Summary({
  areas,
  summary,
  onlineCount,
  offlineCount,
  attentionItems,
  cameraHealthValue,
  countPresentation,
}: {
  areas: MonitoredAreaViewModel[];
  summary: OverviewDecisionSummary | null;
  onlineCount: number | null;
  offlineCount: number | null;
  attentionItems: OverviewV2AttentionItem[];
  cameraHealthValue: string;
  countPresentation: 'measured' | 'unavailable' | 'partial' | 'stale';
}) {
  const hasCurrentControlFollowUp = Boolean(summary?.sinceControl?.controlId)
    && !/no anchor|no control|not available/i.test(
      `${summary?.sinceControl?.followUpState || ''} ${summary?.sinceControl?.answer || ''}`,
    );
  const tiles = buildOverviewV2SummaryTiles({
    zoneCount: areas.length,
    onlineCount,
    offlineCount,
    policyCount: typeof summary?.conditionCount === 'number' ? summary.conditionCount : null,
    patternDecisionCount: recurringReviewCount(areas.flatMap((area) => area.policies.map((policy) => policy.recurring || {}))),
    attentionCount: overviewV2CurrentActivityCount(attentionItems),
    attentionDetail: overviewV2CurrentActivitySummaryDetail(overviewV2CurrentActivityCount(attentionItems)),
    controlFollowUpCount: hasCurrentControlFollowUp ? 1 : 0,
    controlDetail: overviewV2ControlFollowUpDetail({
      controlId: hasCurrentControlFollowUp ? summary?.sinceControl?.controlId : null,
      followUpState: summary?.sinceControl?.followUpState,
      controlName: resolveOverviewV2ControlName({
        answer: summary?.sinceControl?.answer,
        controlLocation: summary?.sinceControl?.controlLocation,
      }),
    }),
    cameraHealthValue,
    availabilityDetail: overviewV2OnlineOfflineDetail(onlineCount, offlineCount),
    countPresentation,
  });
  const testIds: Record<string, string> = {
    zones: 'overview-v2-summary-zones',
    policies: 'overview-v2-summary-policies',
    patterns: 'overview-v2-summary-recurring',
    attention: 'overview-v2-summary-attention',
    controls: 'overview-v2-summary-controls',
    health: 'overview-v2-summary-health',
  };
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: '8px', mb: '10px' }}>
      {tiles.map((tile) => (
        <Metric key={tile.id} label={tile.label} value={tile.value} detail={tile.detail} tone={tile.tone} testId={testIds[tile.id]} />
      ))}
    </Box>
  );
}

function AttentionBanner({ items, onNavigate }: { items: OverviewV2AttentionItem[]; onNavigate: (path: string) => void }) {
  const actionItems = overviewV2BannerActionItems(items);
  if (!actionItems.length) {
    return null;
  }
  const copy = overviewV2SiteAttentionBanner(actionItems);
  const selected = actionItems.find((item) => item.source !== 'coverage' && item.targetPath) || actionItems[0];
  return (
    <Box
      data-testid="overview-v2-attention-panel"
      data-attention-state="open"
      data-condition-id={selected.conditionId}
      data-next-action-kind={selected.nextActionKind || ''}
      sx={{ display: 'flex', alignItems: 'center', gap: '11px', bgcolor: colors.redSoft, border: '1px solid #f2c8c3', borderLeft: `4px solid ${colors.red}`, borderRadius: '9px', p: '9px 11px', mb: '12px' }}
    >
      <Box sx={{ width: 28, height: 28, flex: '0 0 28px', borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#fff0ee', color: colors.red, fontSize: 14, fontWeight: 800 }}>!</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 9, fontWeight: 750, letterSpacing: '.55px', textTransform: 'uppercase', color: colors.red }}>Action required</Box>
        <Box component="b" sx={{ display: 'block', fontSize: 12.5, lineHeight: 1.25, mt: '1px' }}>{copy.title}</Box>
        <Box component="span" sx={{ display: 'block', fontSize: 10.5, color: colors.ink, mt: '2px', lineHeight: 1.35 }}>{copy.sentence}</Box>
      </Box>
      {selected.targetPath ? (
        <OverviewLink
          href={selected.targetPath}
          onNavigate={onNavigate}
          variant="primary"
          testId="overview-v2-attention-cta"
        >
          {selected.ctaLabel || 'Review'}
        </OverviewLink>
      ) : null}
    </Box>
  );
}

function NowOccurrenceTimeline({
  activity,
  timeZone,
  now,
}: {
  activity: OverviewConditionRow['currentActivity'];
  timeZone?: string | null;
  now?: Date | string | null;
}) {
  const timeline = currentActivityTimeline(activity, { timeZone, now: now ?? new Date() });
  const limited = timeline.kind === 'unavailable' || timeline.kind === 'count_only';
  return (
    <Box
      data-testid="overview-v2-now-timeline"
      data-timeline-kind={timeline.kind}
      sx={{ mt: '4px', mb: '8px', bgcolor: limited ? colors.amberSoft : colors.blueSoft, border: `1px solid ${colors.line}`, borderRadius: '6px', p: '6px 8px' }}
    >
      <Box sx={{ fontSize: 9, fontWeight: 700, color: limited ? colors.amber : colors.teal, mb: '4px' }}>Open 15-min window</Box>
      <svg width="100%" height="28" viewBox="0 0 120 28" preserveAspectRatio="none" aria-hidden>
        <rect x="0" y="12" width="120" height="6" rx="3" fill={limited ? '#ead9b8' : '#c9dcf3'} />
        {timeline.marks.map((mark) => (
          <circle
            key={mark.at}
            cx={6 + mark.ratio * 108}
            cy="15"
            r="5"
            fill={limited ? colors.amber : colors.teal}
            stroke="#fff"
            strokeWidth="1.25"
          />
        ))}
      </svg>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: colors.ink3, mt: '2px' }}>
        <span>Window start</span>
        <span>Now</span>
      </Box>
      {timeline.note ? (
        <Box data-testid="overview-v2-now-timeline-note" sx={{ fontSize: 9, color: colors.ink2, mt: '4px', lineHeight: 1.3 }}>
          {timeline.note}
        </Box>
      ) : null}
    </Box>
  );
}

function recurringChartNiceMax(chart: OverviewV2RecurringChart): number {
  const maxValue = Math.max(1, ...chart.buckets.map((bucket) => bucket.plotValue));
  const exp = 10 ** Math.floor(Math.log10(maxValue));
  const n = maxValue / exp;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * exp;
}

function RecurringChartPlot({
  chart,
  patternId,
  density,
  timeZone,
}: {
  chart: OverviewV2RecurringChart;
  patternId: string;
  density: 'compact' | 'expanded';
  timeZone?: string | null;
}) {
  const expanded = density === 'expanded';
  const niceMax = recurringChartNiceMax(chart);
  const plot = expanded
    ? { left: 56, right: 860, top: 28, bottom: 248 }
    : { left: 22, right: 236, top: 8, bottom: 58 };
  const viewW = expanded ? 880 : 240;
  const viewH = expanded ? 280 : 78;
  const axisFont = expanded ? 13 : 6.5;
  const yTickFont = expanded ? 11 : 6.5;
  const peakFont = expanded ? 13 : 7;
  const width = plot.right - plot.left;
  const height = plot.bottom - plot.top;
  const count = Math.max(1, chart.buckets.length);
  const slot = width / count;
  const barWidth = Math.max(expanded ? 8 : 2.4, slot * 0.62);
  const yTicks = [0, niceMax / 2, niceMax];
  const peak = chart.peak;
  const peakX = peak ? plot.left + peak.startIndex * slot : 0;
  const peakW = peak ? Math.max(barWidth, (peak.endIndex - peak.startIndex + 1) * slot) : 0;
  let previousDateLabel: string | null = null;
  return (
    <svg
      width="100%"
      height={expanded ? 280 : 78}
      viewBox={`0 0 ${viewW} ${viewH}`}
      preserveAspectRatio={expanded ? 'xMidYMid meet' : 'none'}
      aria-hidden
      data-testid={expanded ? 'overview-v2-recurring-chart-expanded-plot' : undefined}
      data-chart-density={density}
    >
      <defs>
        <pattern id={patternId} patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
          <rect width="4" height="4" fill={colors.teal} />
          <line x1="0" y1="0" x2="0" y2="4" stroke="#fff" strokeWidth="2" />
        </pattern>
      </defs>
      <rect x={plot.left} y={plot.top} width={width} height={height} rx="2" fill="#fff" />
      {peak ? (
        <rect
          data-testid="overview-v2-recurring-peak-band"
          x={peakX}
          y={plot.top}
          width={peakW}
          height={height}
          fill="#f3e2c4"
          opacity="0.85"
        />
      ) : null}
      {yTicks.map((tick) => {
        const y = plot.bottom - (tick / niceMax) * height;
        return (
          <g key={tick}>
            <line x1={plot.left} x2={plot.right} y1={y} y2={y} stroke={colors.line} strokeWidth="0.6" />
            <text x={plot.left - 3} y={y + 2.5} textAnchor="end" fontSize={yTickFont} fill={colors.ink3}>
              {tick % 1 === 0 ? String(tick) : tick.toFixed(1)}
            </text>
          </g>
        );
      })}
      {chart.buckets.map((bucket, index) => {
        const barHeight = Math.max(bucket.plotValue > 0 ? 2 : 0, (bucket.plotValue / niceMax) * height);
        const x = plot.left + index * slot + (slot - barWidth) / 2;
        const y = plot.bottom - barHeight;
        const expandedTick = expanded
          ? recurringChartExpandedAxisLabel(bucket, {
            grain: chart.grain,
            previousDateLabel,
            timeZone,
          })
          : null;
        if (expandedTick) previousDateLabel = expandedTick.dateLabel;
        const axisText = expanded ? expandedTick?.label : bucket.axisLabel;
        return (
          <g key={bucket.key}>
            {bucket.plotValue > 0 ? (
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="1"
                fill={bucket.isPartial ? `url(#${patternId})` : colors.teal}
                data-partial={bucket.isPartial ? 'true' : 'false'}
                data-testid="overview-v2-recurring-bar"
                data-hour={bucket.label}
                data-usable-hours={bucket.usableHours == null ? '' : String(bucket.usableHours)}
              />
            ) : (
              <rect
                x={x}
                y={plot.bottom}
                width={barWidth}
                height={0}
                fill="none"
                data-testid="overview-v2-recurring-empty-hour"
                data-hour={bucket.label}
              />
            )}
            {axisText ? (
              <text
                x={x + barWidth / 2}
                y={expanded ? viewH - 8 : 72}
                textAnchor="middle"
                fontSize={axisFont}
                fill={colors.ink3}
                data-testid={expanded ? 'overview-v2-recurring-axis-label' : undefined}
                data-hour={bucket.label}
              >
                {axisText}
              </text>
            ) : null}
          </g>
        );
      })}
      {peak ? (
        <text x={peakX + peakW / 2} y={plot.top - 1} textAnchor="middle" fontSize={peakFont} fontWeight="700" fill={colors.amber}>
          {peak.label}
        </text>
      ) : null}
    </svg>
  );
}

function RecurringChartBody({
  chart,
  patternId,
  density,
  timeZone,
  showExpandHint,
}: {
  chart: OverviewV2RecurringChart;
  patternId: string;
  density: 'compact' | 'expanded';
  timeZone?: string | null;
  showExpandHint?: boolean;
}) {
  const expanded = density === 'expanded';
  return (
    <>
      {chart.primaryValue ? (
        <Box sx={{
          fontSize: expanded ? 22 : 15,
          fontWeight: 780,
          color: colors.teal,
          lineHeight: 1.1,
          letterSpacing: '-.3px',
        }}
        >
          {chart.primaryValue}
          <Box component="span" sx={{ fontSize: expanded ? 13 : 9, fontWeight: 650, ml: '5px', color: colors.teal }}>
            {chart.primaryUnit}
          </Box>
        </Box>
      ) : (
        <Box sx={{ fontSize: expanded ? 14 : 10, fontWeight: 700, color: colors.ink2 }}>Occurrence rate unavailable</Box>
      )}
      {chart.secondaryOccurrenceLabel ? (
        <Box sx={{ fontSize: expanded ? 13 : 9, color: colors.ink2, mt: '2px' }}>{chart.secondaryOccurrenceLabel}</Box>
      ) : null}
      {chart.coverageLabel ? (
        <Box sx={{ fontSize: expanded ? 12 : 8.5, color: colors.ink3, mt: '1px' }}>{chart.coverageLabel}</Box>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: expanded ? '10px' : '6px', mb: '2px', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
          <Box sx={{ fontSize: expanded ? 14 : 8, fontWeight: 700, color: colors.ink2 }}>{chart.chartTitle}</Box>
          {showExpandHint ? (
            <Box
              component="span"
              data-testid="overview-v2-recurring-chart-expand"
              sx={{ fontSize: 8, fontWeight: 750, color: colors.blue, letterSpacing: '.2px', flex: '0 0 auto' }}
            >
              Expand
            </Box>
          ) : null}
        </Box>
        <Box sx={{ fontSize: expanded ? 12 : 7.5, color: colors.ink3 }}>{chart.yAxisLabel}</Box>
      </Box>
      <RecurringChartPlot chart={chart} patternId={patternId} density={density} timeZone={timeZone} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mt: expanded ? '8px' : '2px' }}>
        <Box sx={{ display: 'flex', gap: expanded ? '14px' : '8px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: expanded ? 12 : 7.5, color: colors.ink3 }}>
            <Box sx={{ width: expanded ? 12 : 8, height: expanded ? 12 : 8, border: `1px solid ${colors.teal}`, background: `repeating-linear-gradient(135deg, ${colors.teal} 0 2px, #fff 2px 4px)` }} />
            Partial
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: expanded ? 12 : 7.5, color: colors.ink3 }}>
            <Box sx={{ width: expanded ? 12 : 8, height: expanded ? 12 : 8, bgcolor: '#f3e2c4', border: '1px solid #e2c48a' }} />
            Peak period
          </Box>
        </Box>
        {chart.historyBuilding ? (
          <Badge tone="default" variant="outline" testId="overview-v2-recurring-history-building">History building</Badge>
        ) : null}
      </Box>
      {chart.gapNote ? (
        <Box data-testid="overview-v2-recurring-chart-note" sx={{ fontSize: expanded ? 12 : 8.5, color: colors.ink2, mt: '4px', lineHeight: 1.3 }}>
          {chart.gapNote}
        </Box>
      ) : null}
    </>
  );
}

function RecurringOccurrenceChart({
  row,
  windowId,
  timeZone,
  title,
}: {
  row: OverviewPeriodConditionRow | null;
  windowId?: string | null;
  timeZone?: string | null;
  title?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const chart = recurringOccurrenceChart(row, { windowId, timeZone });
  const safeId = String(row?.conditionId || 'none').replace(/[^a-zA-Z0-9_-]/g, '');
  const compactPatternId = `recurring-partial-${safeId}`;
  const expandedPatternId = `recurring-partial-${safeId}-expanded`;
  const titleId = `overview-v2-recurring-chart-title-${safeId}`;
  const conditionTitle = text(title) || 'Safety condition';
  const expandLabel = `Expand ${conditionTitle} ${chart.chartTitle} chart`;
  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid="overview-v2-recurring-chart"
        data-chart-kind={chart.kind}
        data-chart-grain={chart.grain}
        aria-label={expandLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          mt: '4px',
          mb: '8px',
          bgcolor: colors.blueSoft,
          border: `1px solid ${colors.line}`,
          borderRadius: '6px',
          p: '6px 8px',
          minHeight: 72,
          '&:focus-visible': {
            outline: `2px solid ${colors.blue}`,
            outlineOffset: '2px',
          },
        }}
      >
        <RecurringChartBody
          chart={chart}
          patternId={compactPatternId}
          density="compact"
          timeZone={timeZone}
          showExpandHint
        />
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby={titleId}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', p: '12px 16px 8px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box
              component="h2"
              id={titleId}
              data-testid="overview-v2-recurring-chart-dialog-title"
              sx={{ m: 0, fontSize: 16, fontWeight: 750, color: colors.ink }}
            >
              {conditionTitle}
            </Box>
            <Box sx={{ mt: '2px', fontSize: 12, color: colors.ink2 }}>{chart.chartTitle}</Box>
          </Box>
          <IconButton
            aria-label="Close expanded chart"
            data-testid="overview-v2-recurring-chart-close"
            onClick={() => setOpen(false)}
            size="small"
          >
            ×
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          <Box
            data-testid="overview-v2-recurring-chart-dialog"
            data-chart-kind={chart.kind}
            data-chart-grain={chart.grain}
            sx={{
              bgcolor: colors.blueSoft,
              border: `1px solid ${colors.line}`,
              borderRadius: '8px',
              p: '12px 14px',
            }}
          >
            <RecurringChartBody
              chart={chart}
              patternId={expandedPatternId}
              density="expanded"
              timeZone={timeZone}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PolicyStage({ value, detail, facts, mutedFacts, status, statusKind, tone }: OverviewV2Stage) {
  const badgeTone = tone === 'red' ? 'alert' : tone === 'amber' ? 'amber' : tone === 'green' ? 'ok' : 'default';
  const chipVariant = statusKind === 'measurement' ? 'outline' : statusKind === 'decision' ? 'filled' : 'soft';
  return (
    <>
      <Box sx={{ fontSize: 12, fontWeight: 680, lineHeight: 1.25, color: colors.ink }}>{value}</Box>
      <Box sx={{ fontSize: 9.5, color: colors.ink2, mt: '2px', lineHeight: 1.35 }}>{detail}</Box>
      {(facts || []).map((fact) => (
        <Box key={fact} sx={{ fontSize: 9.5, color: colors.ink2, mt: '2px', lineHeight: 1.35 }}>{fact}</Box>
      ))}
      {(mutedFacts || []).map((fact) => (
        <Box key={fact} data-testid="overview-v2-measurement-subordinate" sx={{ fontSize: 8.5, color: colors.ink3, mt: '1px', lineHeight: 1.3 }}>{fact}</Box>
      ))}
      {status ? <Box sx={{ mt: '4px' }}><Badge tone={badgeTone} variant={chipVariant}>{status}</Badge></Box> : null}
    </>
  );
}

function LifecycleStory({
  policy,
  area,
  siteId,
  routes,
  timeZone,
  onNavigate,
}: {
  policy: OverviewPolicy;
  area: MonitoredAreaViewModel;
  siteId: string;
  routes: AreaRoutes;
  timeZone?: string | null;
  onNavigate: (path: string) => void;
}) {
  const lifecycle = policy.recurring?.lifecycle;
  if (!lifecycle) return null;
  return (
    <Box
      data-testid={`overview-v2-lifecycle-${policy.conditionId}`}
      sx={{ mt: '7px', pt: '6px', borderTop: `1px solid ${colors.line}` }}
    >
      <ConditionLifecycleSpine
        lifecycle={lifecycle}
        siteId={siteId}
        conditionId={policy.conditionId}
        areaId={area.areaId}
        zoneId={policyZoneId(area, policy)}
        cameraId={area.cameraId}
        incidentType={policy.current?.incidentType || policy.recurring?.incidentType}
        conditionFamily={policy.current?.conditionFamily || policy.recurring?.conditionFamily}
        controlRecordPath={routes.recordPath}
        legacyControlId={routes.recordedControl?.controlId || policy.current?.sinceControl?.controlId || policy.recurring?.sinceControl?.controlId}
        legacyControlTitle={routes.recordedControl?.title}
        recurrenceLabel="Overview occurrence rate"
        timeZone={timeZone}
        density="om"
        onNavigate={onNavigate}
        testId={`overview-v2-lifecycle-spine-${policy.conditionId}`}
      />
    </Box>
  );
}

function currentStage(
  policy: OverviewPolicy,
  summary: OverviewDecisionSummary | null,
  timeZone?: string | null,
) {
  return currentPolicyStage(policy.current, {
    queryAsOf: summary?.queryAsOf,
    measuredWindow: summary?.now.measuredWindow,
    timeZone,
  });
}

function liveActivityStage(
  policy: OverviewPolicy,
  timeZone?: string | null,
  now?: Date | string | null,
) {
  return currentActivityStage(policy.current?.currentActivity, { timeZone, now: now ?? new Date() });
}

function recurringStage(
  policy: OverviewPolicy,
  summary: OverviewDecisionSummary | null,
  timeZone?: string | null,
) {
  const throughAt = policy.recurring?.includedThroughAt;
  const throughLabel = throughAt
    ? (formatSiteDateTime(throughAt, timeZone) || throughAt)
    : null;
  return recurringPolicyStage(policy.recurring, summary?.overTime.windowLabel, throughLabel);
}

function controlStage(
  policy: OverviewPolicy,
  summary: OverviewDecisionSummary | null,
  timeZone?: string | null,
  recordedControl?: OverviewV2WorkflowIdentity['recordedControl'],
) {
  const lifecycle = policy.recurring?.lifecycle;
  const conditionControl = policy.current?.sinceControl || policy.recurring?.sinceControl;
  const summaryLinked = Boolean(
    summary?.sinceControl.controlId
    && summary.primaryConditionId
    && summary.primaryConditionId === policy.conditionId,
  );
  const omLinked = Boolean(conditionControl?.controlId || summaryLinked);
  const recordedId = text(recordedControl?.controlId);
  const recordedTitle = text(recordedControl?.title);
  const hasRecorded = Boolean(recordedId || recordedTitle);
  const epic402 = Boolean(lifecycle?.controlRevisionId);
  if (!hasRecorded && !omLinked && !epic402) {
    return controlPolicyStage({ linkedToControl: false, controlId: null, controlDateLabel: null });
  }
  const legacyOnly = hasRecorded && !omLinked && !epic402;
  return controlPolicyStage({
    linkedToControl: true,
    controlId: lifecycle?.controlRevisionId
      || recordedId
      || conditionControl?.controlId
      || (summaryLinked ? summary?.sinceControl.controlId : null)
      || null,
    controlDateLabel: lifecycle?.sinceStartedAt
      || recordedControl?.recordedAt
      || conditionControl?.anchorStartedAt
      || (summaryLinked ? summary?.sinceControl.controlDateLabel : null),
    controlName: recordedTitle
      || (epic402 ? 'Recorded control' : null)
      || (conditionControl?.controlId ? 'Recorded control' : null)
      || (summaryLinked ? resolveOverviewV2ControlName({
        answer: summary?.sinceControl.answer,
        controlLocation: summary?.sinceControl.controlLocation,
      }) : null)
      || (legacyOnly ? 'Legacy Control record' : null),
    recordedStatusLabel: recordedControl?.statusLabel
      || (legacyOnly ? 'Legacy Control record — not linked through the condition lifecycle' : null)
      || (epic402 ? 'Linked through condition lifecycle' : null)
      || (conditionControl?.bucketCount ? 'Follow-up measurement recorded' : null),
    followUpState: conditionControl?.controlId
      ? (conditionControl.comparisonStatus === 'not_comparable'
        ? 'Not comparable'
        : conditionControl.bucketCount ? 'Measurement recorded' : 'Awaiting follow-up')
      : summaryLinked ? summary?.sinceControl.followUpState : (epic402 ? 'Linked Control' : null),
    presentationKind: conditionControl?.controlId
      ? (conditionControl.comparisonStatus === 'not_comparable'
        ? 'not_comparable'
        : conditionControl.comparisonStatus === 'comparable'
          ? 'comparison_issuable'
          : 'follow_up_open')
      : summaryLinked ? summary?.sinceControl.presentationKind : undefined,
    basisDifferedLabel: summaryLinked ? summary?.sinceControl.basisDifferedLabel : null,
    comparisonSides: summaryLinked ? summary?.sinceControl.comparisonSides : null,
    followUpThroughLabel: conditionControl?.observedThrough || null,
    timeZone: timeZone || summary?.siteTimezone,
  });
}

const policyCellSx = {
  p: '7px 10px',
  borderRight: { xs: 0, md: `1px solid ${colors.line}` },
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

function StackedLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: { xs: 'block', md: 'none' }, fontSize: 8, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: colors.ink3, mb: '4px' }}>
      {children}
    </Box>
  );
}

function PolicyRow({
  area,
  policy,
  summary,
  siteId,
  workflowIdentity,
  workflowIdentities,
  onNavigate,
  showZoneIdentity,
  visualContext,
  cameraOnline: _cameraOnline,
  attentionWorkflowKeys,
  onOpenZoneView,
  cameraViewOpen: _cameraViewOpen,
  cameraViewFocusZoneId: _cameraViewFocusZoneId,
  onCameraViewOpenChange: _onCameraViewOpenChange,
  onSnapshotOpen: _onSnapshotOpen,
  selectedOverTimeWindowId,
  now,
}: {
  area: MonitoredAreaViewModel;
  policy: OverviewPolicy;
  summary: OverviewDecisionSummary | null;
  siteId: string;
  workflowIdentity?: OverviewV2WorkflowIdentity | null;
  workflowIdentities?: OverviewV2WorkflowIdentity[];
  onNavigate: (path: string) => void;
  showZoneIdentity: boolean;
  visualContext?: OverviewAreaCameraPanelModel;
  cameraOnline?: boolean;
  attentionWorkflowKeys?: Iterable<string>;
  onOpenZoneView?: (zoneId: string | null) => void;
  cameraViewOpen?: boolean;
  cameraViewFocusZoneId?: string | null;
  onCameraViewOpenChange?: (open: boolean) => void;
  onSnapshotOpen?: () => void;
  selectedOverTimeWindowId?: OverTimeWindowId;
  now?: Date | string | null;
}) {
  const loadedSiteTimeZone = useSiteTimeZone(siteId);
  const siteTimeZone = summary?.siteTimezone || loadedSiteTimeZone;
  const routes = routesForPolicy(siteId, area, policy, workflowIdentity, workflowIdentities);
  const live = liveActivityStage(policy, siteTimeZone, now);
  const lastCompleted = currentStage(policy, summary, siteTimeZone);
  const attentionOwnsNextAction = overviewV2AttentionOwnsZoneReview(attentionWorkflowKeys || [], {
    conditionId: policy.conditionId,
    evidencePath: routes.evidencePath,
    casePath: routes.casePath,
  });
  const zoneAction = resolveOverviewV2ZonePrimaryAction({
    attentionOwnsNextAction,
    attentionRequired: policy.current?.attentionRequired,
    isCanonical: policy.isCanonical,
    evidencePath: routes.evidencePath,
    casePath: routes.casePath,
    zonePath: routes.zonePath,
    createCasePath: routes.createCasePath,
  });
  const coverageIncomplete = coverageIncompleteFromRow(policy.current);
  const composite = isCompositePolicy(policy);
  const spatial = composite
    ? { kind: 'CAMERA' as const }
    : overviewV2ConditionSpatialScope({
      incidentType: policy.current?.incidentType || policy.recurring?.incidentType,
      zoneId: policyZoneId(area, policy),
    });
  const measurementScope = composite
    ? (policy.current?.location || policy.recurring?.location || area.location)
    : overviewV2MeasurementScopeLabel({
      incidentType: policy.current?.incidentType || policy.recurring?.incidentType,
      zoneId: policyZoneId(area, policy),
      zoneLabel: visualContext?.overlays?.find((item) => item.zoneId === policyZoneId(area, policy))?.label
        || visualContext?.zoneLabel
        || customerZoneLabel({
          zoneName: overviewV2PublicZoneId(policyZoneId(area, policy)),
          location: area.location,
        }),
    });
  const openZoneInViewer = Boolean(
    onOpenZoneView
    && zoneAction.primaryLabel === 'Open zone'
    && spatial.kind === 'ZONE_POLYGON'
    && visualContext?.snapshotUrl,
  );
  return (
    <Box
      data-testid="overview-v2-policy-row"
      data-condition-id={policy.conditionId}
      data-subject-kind={isCompositePolicy(policy) ? 'COMPOSITE' : 'PHYSICAL'}
      data-zone-id={isCompositePolicy(policy) ? '' : (policyZoneId(area, policy) || '')}
      sx={{
        display: 'grid',
        gridTemplateColumns: zoneMatrixColumns,
        borderBottom: `1px solid ${colors.line}`,
        minHeight: 54,
        bgcolor: policy.priority ? '#fffdfd' : '#fff',
      }}
    >
      <Box sx={policyCellSx}>
        <StackedLabel>Camera / Condition</StackedLabel>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', pl: { xs: '10px', md: '4px' } }}>
          <Box sx={{ width: 25, height: 25, flex: '0 0 25px', borderRadius: '6px', bgcolor: policy.priority ? '#fff0ee' : colors.greySoft, display: 'grid', placeItems: 'center', fontSize: 8.5, fontWeight: 750, color: policy.priority ? colors.red : colors.ink2 }}>{policy.icon}</Box>
          <Box>
            <Box component="b" sx={{ display: 'block', fontSize: 11.5, fontWeight: 680, lineHeight: 1.2 }}>{policy.label}</Box>
            {policy.subtitle ? (
              <Box component="span" sx={{ display: 'block', fontSize: 9.5, color: colors.ink3, mt: '2px' }}>{policy.subtitle}</Box>
            ) : null}
            <Box
              data-testid="overview-v2-measurement-scope"
              data-scope-kind={spatial.kind}
              sx={{ display: 'block', fontSize: 9.5, color: colors.ink2, mt: '3px' }}
            >
              {measurementScope}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={policyCellSx} data-testid="overview-v2-now-cell">
        <StackedLabel>Current activity</StackedLabel>
        <NowOccurrenceTimeline activity={policy.current?.currentActivity} timeZone={siteTimeZone} now={now} />
        <PolicyStage {...live} />
        <Box data-testid="overview-v2-last-completed" sx={{ mt: '8px' }}>
          <Box sx={{ fontSize: 8, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: colors.ink3, mb: '4px' }}>
            Last completed measurement
          </Box>
          <PolicyStage {...lastCompleted} />
        </Box>
        {policy.current?.attentionRequired ? (
          <Box sx={{ mt: '4px' }}>
            <Badge tone="alert" variant="filled" testId="overview-v2-now-attention-chip">Action required</Badge>
          </Box>
        ) : coverageIncomplete && !lastCompleted.status ? (
          <Box sx={{ mt: '4px' }}>
            <Badge tone="default" variant="outline" testId="overview-v2-now-coverage-chip">Coverage incomplete</Badge>
          </Box>
        ) : null}
      </Box>
      <Box sx={policyCellSx} data-testid="overview-v2-recurring-cell">
        <StackedLabel>Recurring</StackedLabel>
        {isCompositePolicy(policy) ? (
          <>
            <PolicyStage {...recurringStage(policy, summary, siteTimeZone)} />
            <LifecycleStory
              policy={policy}
              area={area}
              siteId={siteId}
              routes={routes}
              timeZone={siteTimeZone}
              onNavigate={onNavigate}
            />
          </>
        ) : (
          <>
            <RecurringOccurrenceChart
              row={policy.recurring}
              windowId={selectedOverTimeWindowId || summary?.overTime.selectedWindowId}
              timeZone={siteTimeZone}
              title={policy.label}
            />
            {recurringOccurrenceChart(
              policy.recurring,
              { windowId: selectedOverTimeWindowId || summary?.overTime.selectedWindowId, timeZone: siteTimeZone },
            ).primaryValue ? null : (
              <PolicyStage {...recurringStage(policy, summary, siteTimeZone)} />
            )}
            <LifecycleStory
              policy={policy}
              area={area}
              siteId={siteId}
              routes={routes}
              timeZone={siteTimeZone}
              onNavigate={onNavigate}
            />
          </>
        )}
      </Box>
      <Box sx={policyCellSx} data-testid="overview-v2-control-cell">
        <StackedLabel>Control</StackedLabel>
        <PolicyStage {...controlStage(policy, summary, siteTimeZone, routes.recordedControl)} />
      </Box>
      <Box data-testid="overview-v2-action-cell" sx={{ p: '7px 8px', display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'center', gap: '5px' }}>
        <StackedLabel>Action</StackedLabel>
        {isCompositePolicy(policy) ? (
          <>
            {routes.evidencePath && policy.current?.subjectKind === 'COMPOSITE' ? (
              <OverviewLink
                href={routes.evidencePath}
                onNavigate={onNavigate}
                variant="primary"
                testId="overview-v2-composite-details"
              >
                Details
              </OverviewLink>
            ) : routes.casePath ? (
              <OverviewLink
                href={routes.casePath}
                onNavigate={onNavigate}
                variant="primary"
                testId="overview-v2-composite-review"
              >
                Review
              </OverviewLink>
            ) : (
              <OverviewLink href="#" variant="primary" testId="overview-v2-composite-details">
                Details
              </OverviewLink>
            )}
          </>
        ) : openZoneInViewer ? (
          <Box
            component="button"
            type="button"
            data-testid="overview-v2-primary-action"
            data-action-variant="primary"
            onClick={() => onOpenZoneView?.(policyZoneId(area, policy))}
            sx={{
              color: '#fff',
              fontWeight: 750,
              fontSize: 10,
              bgcolor: colors.blue,
              border: `1px solid ${colors.blue}`,
              borderRadius: '6px',
              p: '6px 10px',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            {zoneAction.primaryLabel}
          </Box>
        ) : (
          <OverviewLink
            href={zoneAction.primaryHref || '#'}
            onNavigate={zoneAction.primaryHref ? onNavigate : undefined}
            variant="primary"
            testId="overview-v2-primary-action"
          >
            {zoneAction.primaryLabel}
          </OverviewLink>
        )}
        <Box data-testid={showZoneIdentity ? 'overview-v2-workflow-breadcrumbs' : undefined} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: '3px' }}>
          {isCompositePolicy(policy) && routes.casePath && policy.current?.subjectKind === 'COMPOSITE' ? (
            <OverviewLink href={routes.casePath} onNavigate={onNavigate} sx={{ fontSize: 9.5, fontWeight: 600 }} testId="overview-v2-composite-review">Review</OverviewLink>
          ) : null}
          {isCompositePolicy(policy) && routes.recordPath ? (
            <OverviewLink
              href={routes.recordPath}
              onNavigate={onNavigate}
              sx={{ fontSize: 9.5, fontWeight: 600 }}
              testId="overview-v2-composite-record-control"
            >
              {routes.recordedControl ? 'View control' : 'Record control'}
            </OverviewLink>
          ) : null}
          {isCompositePolicy(policy) ? null : zoneAction.showInvestigate ? <OverviewLink href={routes.evidencePath as string} onNavigate={onNavigate} sx={{ fontSize: 9.5, fontWeight: 600 }}>Investigate</OverviewLink> : null}
          {isCompositePolicy(policy) ? null : zoneAction.showCaseAction ? (
            <OverviewLink href={zoneAction.caseHref} onNavigate={onNavigate} sx={{ fontSize: 9.5, fontWeight: 600 }} testId="overview-v2-case-action">{zoneAction.caseLabel}</OverviewLink>
          ) : null}
          {isCompositePolicy(policy) ? null : routes.recordPath ? <OverviewLink href={routes.recordPath} onNavigate={onNavigate} sx={{ fontSize: 9.5, fontWeight: 600 }}>Record</OverviewLink> : null}
        </Box>
      </Box>
    </Box>
  );
}

function ZoneSafetyStory({
  area,
  policy,
  summary,
  timeZone,
  recordedControl,
}: {
  area: MonitoredAreaViewModel;
  policy: OverviewPolicy;
  summary: OverviewDecisionSummary | null;
  timeZone?: string | null;
  recordedControl?: OverviewV2WorkflowIdentity['recordedControl'];
}) {
  const conditionControl = policy.current?.sinceControl || policy.recurring?.sinceControl;
  const summaryLinked = Boolean(
    summary?.sinceControl.controlId
    && summary.primaryConditionId
    && summary.primaryConditionId === policy.conditionId,
  );
  const omLinked = Boolean(conditionControl?.controlId || summaryLinked);
  const recordedTitle = text(recordedControl?.title);
  const linked = omLinked || Boolean(recordedTitle);
  const story = composeZoneSafetyStory({
    policyLabel: policy.label,
    comparisonStatus: policy.recurring?.comparisonStatus,
    linkedToControl: linked,
    controlName: recordedTitle || (conditionControl?.controlId ? 'Recorded control' : null)
      || (summaryLinked ? resolveOverviewV2ControlName({
        answer: summary?.sinceControl.answer,
        controlLocation: summary?.sinceControl.controlLocation,
      }) : null),
    controlDateLabel: recordedControl?.recordedAt || conditionControl?.anchorStartedAt
      || (summaryLinked ? summary?.sinceControl.controlDateLabel : null),
    followUpState: conditionControl?.controlId
      ? (conditionControl.comparisonStatus === 'not_comparable'
        ? 'Not comparable'
        : conditionControl.bucketCount ? 'Measurement recorded' : 'Awaiting follow-up')
      : summary?.sinceControl.followUpState,
    coverageIncomplete: coverageIncompleteFromRow(policy.current),
    hasRecordedHistory: Boolean(
      policy.recurring
      && (typeof policy.recurring.recordedOccurrenceTotal === 'number'
        || text(policy.recurring.comparisonStatus)),
    ),
    timeZone: timeZone || summary?.siteTimezone,
  });
  return (
    <Box
      data-testid="overview-v2-safety-story"
      data-area-key={area.canonicalScopeKey}
      sx={{ bgcolor: '#fcfdfe', p: { xs: '8px 12px 9px', md: '8px 14px 10px' }, borderTop: `1px solid ${colors.line}` }}
    >
      <Box
        data-testid={policy.isCanonical ? 'overview-v2-expanded-row' : 'overview-v2-safety-story-body'}
        sx={{ fontSize: 11, color: colors.ink2, lineHeight: 1.45, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
      >
        <Box component="b" sx={{ color: colors.ink, fontWeight: 700 }}>Safety story:</Box>{' '}{story}
      </Box>
    </Box>
  );
}

function buildVisualContextModel(args: {
  area: MonitoredAreaViewModel;
  camera: Camera | null;
  thumbnail: string | null;
  snapshotPending?: boolean;
  overlays: OverviewAreaGeometry[];
  siteId: string;
  fixtureProvenance?: boolean;
}): OverviewAreaCameraPanelModel {
  const overlays = args.overlays.filter((item) => item.polygon && item.polygon.length >= 3);
  const geometry = overlays[0] || null;
  const visibleMount = overviewV2VisibleMountLabel(args.area.areaName, args.area.mountLabel);
  return {
    areaTitle: args.area.title,
    cameraLabel: areaCameraLabel(args.area, args.camera),
    cameraId: args.area.cameraId,
    deviceId: args.area.deviceId,
    zoneLabel: customerZoneLabel({
      zoneName: geometry?.label || overviewV2PublicZoneId(args.area.zoneId),
      location: args.area.location,
    }),
    zoneId: overviewV2PublicZoneId(args.area.zoneId),
    snapshotUrl: args.thumbnail,
    snapshotPending: args.snapshotPending,
    geometry,
    overlays,
    cameraPath: buildCameraPath(args.siteId, args.area),
    livePath: buildLiveViewPath(args.siteId, args.area),
    zonePath: buildZonePath(args.siteId, args.area),
    showFixtureProvenance: Boolean(args.fixtureProvenance),
    areaName: args.area.areaName || null,
    mountLabel: visibleMount,
  };
}

function inventoryDeviceIdForArea(area: MonitoredAreaViewModel, camera: Camera | null): string | null {
  if (!area.cameraId || !camera) return null;
  if (text(camera.camera_id) !== area.cameraId) return null;
  return text(camera.edge_device_id) || null;
}

function AreaCard({
  area,
  summary,
  camera,
  cameraOnline,
  thumbnail,
  snapshotPending,
  overlays,
  fixtureProvenance,
  siteId,
  workflowIdentity,
  workflowIdentities,
  onNavigate,
  attentionWorkflowKeys,
  cameraMeasurementOverride = null,
  cameraMeasurementEnabled = true,
  selectedOverTimeWindowId,
  now,
}: {
  area: MonitoredAreaViewModel;
  summary: OverviewDecisionSummary | null;
  camera: Camera | null;
  cameraOnline: boolean;
  thumbnail: string | null;
  snapshotPending?: boolean;
  overlays: OverviewAreaGeometry[];
  fixtureProvenance?: boolean;
  siteId: string;
  workflowIdentity?: OverviewV2WorkflowIdentity | null;
  workflowIdentities?: OverviewV2WorkflowIdentity[];
  onNavigate: (path: string) => void;
  attentionWorkflowKeys?: Iterable<string>;
  cameraMeasurementOverride?: CameraActivityMeasurementResponse | null;
  cameraMeasurementEnabled?: boolean;
  selectedOverTimeWindowId?: OverTimeWindowId;
  now?: Date | string | null;
}) {
  const priority = area.policies.some((policy) => policy.priority || policy.current?.attentionRequired);
  const visualContext = buildVisualContextModel({
    area,
    camera,
    thumbnail,
    snapshotPending,
    overlays,
    siteId,
    fixtureProvenance,
  });
  const primary = area.policies[0];
  const loadedSiteTimeZone = useSiteTimeZone(siteId);
  const siteTimeZone = summary?.siteTimezone || loadedSiteTimeZone;
  const exactInventoryDeviceId = inventoryDeviceIdForArea(area, camera);
  const [cameraView, setCameraView] = useState<{ open: boolean; focusZoneId: string | null }>({
    open: false,
    focusZoneId: null,
  });
  return (
    <Box
      component="section"
      data-testid="overview-v2-area-card"
      data-area-key={area.canonicalScopeKey}
      data-camera-id={area.cameraId || ''}
      data-device-id={area.deviceId || ''}
      sx={{ bgcolor: '#fff', border: `1px solid ${priority ? '#d9e3fb' : colors.line}`, borderRadius: '9px', mb: '10px', overflow: 'hidden', boxShadow: priority ? '0 1px 0 rgba(15,23,42,.02)' : 'none' }}
    >
      <Box data-testid="overview-v2-camera-group" sx={{ bgcolor: '#fff' }}>
        <Box
          data-testid="overview-v2-camera-header"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            p: '10px 12px 8px',
            bgcolor: '#f8fafc',
            borderBottom: `1px solid ${colors.line}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Box sx={{ width: 28, height: 28, flex: '0 0 28px', borderRadius: '8px', bgcolor: colors.blueSoft, display: 'grid', placeItems: 'center', color: colors.blue, fontWeight: 750, fontSize: 11 }}>
              {area.title.match(/Room\s+([A-Z])/i)?.[1] || area.title[0] || 'C'}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: 8, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: colors.ink3 }}>Camera</Box>
              <Box component="h3" data-testid="overview-v2-area-title" sx={{ m: 0, fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{area.title}</Box>
              <Box component="p" data-testid="overview-v2-camera-zone-boundary" sx={{ m: '2px 0 0', fontSize: 10, color: colors.ink2 }}>{visualContext.cameraLabel}</Box>
            </Box>
            <Badge tone={cameraOnline ? 'ok' : 'amber'} testId="overview-v2-camera-status">{cameraOnline ? 'Camera online' : 'Camera unavailable'}</Badge>
            <Badge>{area.policies.length} {area.policies.length === 1 ? 'condition' : 'conditions'} active</Badge>
          </Box>
          <OverviewAreaCameraPanel
            model={visualContext}
            onNavigate={onNavigate}
            variant="compact"
            viewerOpen={cameraView.open}
            focusZoneId={cameraView.focusZoneId}
            onViewerOpenChange={(open) => setCameraView((current) => (
              open ? current : { open: false, focusZoneId: null }
            ))}
            onSnapshotOpen={() => setCameraView({ open: true, focusZoneId: null })}
          />
        </Box>
        <CameraMeasurementPanel
          siteId={siteId}
          deviceId={area.deviceId}
          cameraId={area.cameraId}
          inventoryDeviceId={exactInventoryDeviceId}
          zoneName={visualContext.zoneLabel}
          presentation="compact"
          defaultPreset="last_complete_15m"
          defaultScope="CAMERA"
          override={cameraMeasurementOverride}
          enabled={cameraMeasurementEnabled}
          timeZone={siteTimeZone}
        />
        <Box
          data-testid="overview-v2-safety-conditions"
          sx={{
            borderTop: `1px solid ${colors.line}`,
            ml: { xs: '8px', md: '14px' },
            borderLeft: `3px solid ${colors.blueSoft}`,
            bgcolor: '#fcfdff',
          }}
        >
          <Box sx={{ px: '12px', pt: '7px', pb: '4px', fontSize: 8, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: colors.ink3, bgcolor: '#fcfdff' }}>
            Safety conditions
          </Box>
          {area.policies.map((policy, index) => (
            <PolicyRow
              key={policy.conditionId}
              area={area}
              policy={policy}
              summary={summary}
              siteId={siteId}
              workflowIdentity={workflowIdentity}
              workflowIdentities={workflowIdentities}
              onNavigate={onNavigate}
              showZoneIdentity={index === 0}
              visualContext={visualContext}
              cameraOnline={cameraOnline}
              attentionWorkflowKeys={attentionWorkflowKeys}
              onOpenZoneView={(zoneId) => setCameraView({ open: true, focusZoneId: zoneId })}
              cameraViewOpen={cameraView.open}
              cameraViewFocusZoneId={cameraView.focusZoneId}
              onCameraViewOpenChange={(open) => setCameraView((current) => (
                open ? current : { open: false, focusZoneId: null }
              ))}
              onSnapshotOpen={() => setCameraView({ open: true, focusZoneId: null })}
              selectedOverTimeWindowId={selectedOverTimeWindowId}
              now={now}
            />
          ))}
        </Box>
        {primary ? (
          <ZoneSafetyStory
            area={area}
            policy={primary}
            summary={summary}
            timeZone={siteTimeZone}
            recordedControl={resolvePolicyWorkflow(area, primary, workflowIdentity, workflowIdentities).recordedControl}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function AttentionQueue({
  items,
  bannerItem,
  onNavigate,
}: {
  items: OverviewV2AttentionItem[];
  bannerItem?: OverviewV2AttentionItem | null;
  onNavigate: (path: string) => void;
}) {
  return (
    <Box id="overview-v2-attention-queue" data-testid="overview-v2-attention-queue" sx={{ bgcolor: '#fff', border: `1px solid ${colors.line}`, borderRadius: '9px', p: '12px', minWidth: 0 }}>
      <Box component="h3" sx={{ m: 0, fontSize: 12, fontWeight: 650, color: colors.ink2 }}>Action required</Box>
      <Box sx={{ fontSize: 10, color: colors.ink3, mt: '2px', mb: '10px' }}>Complete current attention queue. Measurement counts stay in the zone matrix.</Box>
      {items.length ? items.map((item) => {
        const sameWorkflow = Boolean(
          bannerItem
          && item.conditionId === bannerItem.conditionId
          && item.targetPath
          && item.targetPath === bannerItem.targetPath,
        );
        const showCta = Boolean(item.targetPath) && !sameWorkflow;
        return (
        <Box key={item.id} data-testid="overview-v2-attention-queue-item" data-condition-id={item.conditionId} data-attention-source={item.source} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', py: '7px', borderTop: `1px solid ${colors.line}` }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11, fontWeight: 600, color: colors.ink }}>{item.reason}</Box>
            <Box sx={{ fontSize: 9.5, color: colors.ink3, mt: '2px' }}>
              {item.affectedPolicies?.length
                ? `Affects: ${item.affectedPolicies.join(', ')}`
                : (item.location || item.detail || 'Work waiting on a person')}
            </Box>
          </Box>
          {showCta ? <OverviewLink href={item.targetPath as string} onNavigate={onNavigate} sx={{ fontSize: 9.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{item.ctaLabel || 'Review'}</OverviewLink> : null}
        </Box>
        );
      }) : (
        <Box sx={{ fontSize: 11, color: colors.ink2 }}>No person-owned work is open.</Box>
      )}
    </Box>
  );
}

function MeasurementHealthPanel({
  summary,
  rows,
  cameraHealthValue,
  freshnessLabel,
}: {
  summary: OverviewDecisionSummary | null;
  rows: Array<OverviewV2CameraHealthRow & { rowKey: string }>;
  cameraHealthValue: string;
  freshnessLabel: string;
}) {
  const health = summary?.measurementHealth;
  const methodology = summary?.methodology;
  return (
    <Box data-testid="overview-v2-measurement-health" sx={{ bgcolor: '#fff', border: `1px solid ${colors.line}`, borderRadius: '9px', p: '12px', minWidth: 0 }}>
        <Box data-testid="overview-v2-system-status" sx={{ mb: '10px' }}>
        <Box component="h3" sx={{ m: 0, fontSize: 13, fontWeight: 700 }}>Measurement health</Box>
        <Box sx={{ fontSize: 10, color: colors.ink3, mt: '2px' }}>Camera availability — online cameras and freshness, not Camera Measurement quality.</Box>
        <Box sx={{ fontSize: 10, color: colors.ink2, mt: '6px' }}>
          Cameras available <Box component="b">{cameraHealthValue}</Box>
          {' · '}
          Freshness <Box component="b">{freshnessLabel || 'Unavailable'}</Box>
        </Box>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1.1fr 1.1fr 0.7fr 1.1fr 0.9fr', fontSize: 8.5, fontWeight: 750, letterSpacing: '.55px', textTransform: 'uppercase', color: colors.ink3, pb: '6px', borderBottom: `1px solid ${colors.line}` }}>
        <div>Camera</div>
        <div>Location</div>
        <div>Online</div>
        <div>Measuring</div>
        <div>Freshness</div>
      </Box>
      {rows.length ? rows.map((row) => (
        <Box key={row.rowKey} data-testid="overview-v2-camera-health-row" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.1fr 0.7fr 1.1fr 0.9fr' }, gap: { xs: '2px', md: 0 }, py: '8px', borderBottom: `1px solid ${colors.line}`, fontSize: 11 }}>
          <Box><b>{row.cameraLabel}</b></Box>
          <Box sx={{ color: colors.ink2 }}>{row.location}</Box>
          <Box>{row.status}</Box>
          <Box sx={{ color: colors.ink2 }}>{row.measuring}</Box>
          <Box sx={{ color: colors.ink2 }}>{row.freshness}</Box>
        </Box>
      )) : (
        <Box sx={{ fontSize: 11, color: colors.ink2, pt: '8px' }}>Camera health rows are unavailable.</Box>
      )}
      <Box component="details" data-testid="overview-v2-measurement-details" sx={{ mt: '10px' }}>
        <Box component="summary" sx={{ cursor: 'pointer', fontSize: 10.5, color: colors.blue, fontWeight: 700 }}>View measurement details</Box>
        <Box sx={{ fontSize: 10, color: colors.ink2, mt: '6px', lineHeight: 1.45 }}>
          {health?.coverageLabel ? <Box>Coverage: {health.coverageLabel}</Box> : null}
          {health?.freshnessLabel ? <Box>Freshness: {health.freshnessLabel}</Box> : null}
          {health?.issues?.length
            ? health.issues.map((issue) => <Box key={issue.id}>{issue.label}</Box>)
            : <Box>No open measurement issues.</Box>}
        </Box>
      </Box>
      <Box component="details" data-testid="overview-v2-how-measured" sx={{ mt: '8px' }}>
        <Box component="summary" sx={{ cursor: 'pointer', fontSize: 10.5, color: colors.blue, fontWeight: 700 }}>How this is measured</Box>
        <Box sx={{ fontSize: 10, color: colors.ink2, mt: '6px', lineHeight: 1.45 }}>
          Measurement values are methodology context only — never incidents, severity, or risk conclusions.
          {methodology?.nowLimitations ? ` Current limitations: ${methodology.nowLimitations}.` : ''}
          {methodology?.overTimeLimitations ? ` Recurring limitations: ${methodology.overTimeLimitations}.` : ''}
        </Box>
      </Box>
    </Box>
  );
}

type OverviewV2AreaCentricViewProps = {
  siteId: string;
  summary: OverviewDecisionSummary | null;
  onNavigate: (path: string) => void;
  binding?: OverviewV2Binding;
  workflowIdentity?: OverviewV2WorkflowIdentity | null;
  workflowIdentities?: OverviewV2WorkflowIdentity[];
  cameraAvailabilityById?: Record<string, boolean>;
  cameraEvidenceOverride?: OverviewCameraEvidenceState;
  fixtureZones?: Zone[];
  selectedOverTimeWindowId?: OverTimeWindowId;
  onOverTimeWindowChange?: (windowId: OverTimeWindowId) => void;
  cameraMeasurementOverrides?: Record<string, CameraActivityMeasurementResponse>;
  cameraPlacements?: Record<string, OverviewV2CameraPlacement>;
  readState?: 'authoritative' | 'unavailable' | 'partial' | 'stale';
};

function AreaGroupHeader({
  group,
  expanded,
  onToggle,
}: {
  group: { key: string; areaId: string | null; areaLabel: string; cameras: MonitoredAreaViewModel[] };
  expanded: boolean;
  onToggle: () => void;
}) {
  const counts = overviewV2AreaGroupCounts(group.cameras);
  const summary = overviewV2AreaCollapsedSummary(counts);
  const panelId = `overview-v2-area-panel-${group.key}`;
  return (
    <Box
      component="button"
      type="button"
      data-testid="overview-v2-area-group-toggle"
      aria-expanded={expanded}
      aria-controls={panelId}
      onClick={onToggle}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
        textAlign: 'left',
        fontSize: 11,
        fontWeight: 750,
        color: colors.ink2,
        bgcolor: colors.bg,
        border: 0,
        borderLeft: `1px solid ${colors.line}`,
        borderRight: `1px solid ${colors.line}`,
        px: '10px',
        py: '6px',
        cursor: 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${colors.blue}`,
          outlineOffset: '-2px',
        },
      }}
    >
      <Box>
        <Box data-testid="overview-v2-area-group-title" data-place-kind="area">
          <Box component="span" aria-hidden sx={{ mr: '6px' }}>{expanded ? '▾' : '▸'}</Box>
          Area · {group.areaLabel}
        </Box>
        <Box data-testid="overview-v2-area-group-summary" sx={{ mt: '2px', fontSize: 10, fontWeight: 600, color: colors.ink3 }}>
          {summary}
        </Box>
      </Box>
    </Box>
  );
}

export const OverviewV2AreaCentricView: React.FC<OverviewV2AreaCentricViewProps> = ({
  siteId,
  summary,
  onNavigate,
  binding = 'live',
  workflowIdentity = null,
  workflowIdentities,
  cameraAvailabilityById,
  cameraEvidenceOverride,
  fixtureZones,
  selectedOverTimeWindowId,
  onOverTimeWindowChange,
  cameraMeasurementOverrides,
  cameraPlacements,
  readState,
}) => {
  const loadedSiteTimeZone = useSiteTimeZone(siteId);
  const siteTimeZone = summary?.siteTimezone || loadedSiteTimeZone;
  const now = useOpenSitePt15mClock(siteTimeZone);
  const allowFixture = binding === 'fixture';
  const activeFixtureZones = allowFixture ? fixtureZones : undefined;
  const activeCameraOverride = allowFixture ? cameraEvidenceOverride : undefined;
  const activeCameraAvailability = allowFixture ? cameraAvailabilityById : undefined;
  const liveLookups = useSiteLocationLookups(siteId, Boolean(siteId) && !allowFixture && !cameraPlacements);
  const livePlacementById = liveLookups?.cameraPlacementById;
  const resolvedPlacements = useMemo(() => {
    if (cameraPlacements) return cameraPlacements;
    const byId = livePlacementById;
    if (!byId || !Object.keys(byId).length) return EMPTY_CAMERA_PLACEMENTS;
    const next: Record<string, OverviewV2CameraPlacement> = {};
    Object.entries(byId).forEach(([id, placement]) => {
      next[id] = {
        areaId: placement.areaId,
        areaName: placement.areaName,
        mountLabel: placement.mountLabel,
        deviceId: placement.deviceId,
      };
    });
    return next;
  }, [cameraPlacements, livePlacementById]);
  const areas = useMemo(() => buildAreas(summary, resolvedPlacements, binding), [summary, resolvedPlacements, binding]);
  const spatialGroups = useMemo(
    () => groupOverviewV2AreasBySpatialScope(areas, resolvedPlacements),
    [areas, resolvedPlacements],
  );
  const groupMeta = useMemo(
    () => spatialGroups.map((group) => ({
      key: group.key,
      needsAttention: overviewV2AreaNeedsAttention(group.cameras),
    })),
    [spatialGroups],
  );
  const groupKeys = groupMeta.map((group) => group.key).join('|');
  const [expansion, setExpansion] = useState<Record<string, boolean> | null>(() => (
    readOverviewV2AreaExpansionState(siteId)
  ));
  useEffect(() => {
    setExpansion(readOverviewV2AreaExpansionState(siteId));
  }, [siteId]);
  useEffect(() => {
    if (!groupMeta.length) return undefined;
    setExpansion((current) => mergeOverviewV2AreaExpansionState(groupMeta, current));
    return undefined;
    // groupKeys is the stable identity of groupMeta; the array itself is new each memo pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- groupMeta is represented by groupKeys
  }, [siteId, groupKeys]);
  const resolvedExpansion = expansion || mergeOverviewV2AreaExpansionState(groupMeta, null);
  const toggleArea = (areaKey: string) => {
    setExpansion((current) => {
      const next = mergeOverviewV2AreaExpansionState(groupMeta, current);
      next[areaKey] = !next[areaKey];
      writeOverviewV2AreaExpansionState(siteId, next);
      return next;
    });
  };
  const linkedByCamera = useMemo(() => areas.reduce<Record<string, number>>((acc, area) => { if (area.cameraId) acc[area.cameraId] = 1; return acc; }, {}), [areas]);
  const requiredCameras = useMemo(
    () => areas.flatMap((area) => (area.cameraId ? [{ cameraId: area.cameraId, deviceId: area.deviceId }] : [])),
    [areas],
  );
  const loadedCameraEvidence = useOverviewCameraEvidence({
    siteId,
    linkedByCamera,
    requiredCameras,
    enabled: Boolean(siteId && !activeCameraOverride),
  });
  const cameraEvidence = activeCameraOverride || loadedCameraEvidence;
  const evidenceThumbRequests = useMemo(() => {
    if (allowFixture || !summary) return [];
    return collectOverviewEvidenceThumbRequests({
      nowRows: summary.now.conditionRows || [],
      recurringRows: summary.overTime.conditionRows || [],
    });
  }, [allowFixture, summary]);
  const evidenceThumbs = useOverviewConditionEvidenceThumbs({
    siteId,
    requests: evidenceThumbRequests,
    enabled: Boolean(siteId && !allowFixture && evidenceThumbRequests.length > 0),
  })?.byConditionId || {};
  const overlaysByArea = useOverviewDeployedOverlays(siteId, areas, activeFixtureZones);
  const attentionItems = useMemo(
    () => selectOverviewV2AttentionItems({
      policies: collectPolicyAttentionArgs(areas, siteId, workflowIdentity, workflowIdentities),
      openWork: summary?.openWork?.items,
      decisionActions: summary?.decisionActions,
    }),
    [areas, siteId, summary, workflowIdentity, workflowIdentities],
  );
  const personOwnedAttention = useMemo(
    () => attentionItems.filter((item) => item.source !== 'coverage'),
    [attentionItems],
  );
  const attentionWorkflowKeys = useMemo(
    () => new Set(
      personOwnedAttention
        .map((item) => overviewV2NextActionWorkflowKey({
          conditionId: item.conditionId,
          nextActionKind: item.nextActionKind,
          targetPath: item.targetPath,
        }))
        .filter((key): key is string => Boolean(key)),
    ),
    [personOwnedAttention],
  );

  const areaCameraState = areas.map((area) => {
    const camera = selectOverviewV2CameraForArea(cameraEvidence.cameras, area);
    const fixtureOnline = area.cameraId && activeCameraAvailability && area.cameraId in activeCameraAvailability
      ? activeCameraAvailability[area.cameraId]
      : undefined;
    const cameraOnline = allowFixture
      ? (fixtureOnline ?? Boolean(camera))
      : camera
        ? isCameraOnlineForEvidence(camera)
        : false;
    return { area, camera, cameraOnline };
  });
  const knownStatuses = areaCameraState.filter((row) => row.area.cameraId);
  const onlineCount = knownStatuses.length ? knownStatuses.filter((row) => row.cameraOnline).length : null;
  const offlineCount = knownStatuses.length && onlineCount != null ? knownStatuses.length - onlineCount : null;
  const siteHealthFacts = buildOverviewV2CameraHealthFacts({
    cameraId: areaCameraState[0]?.area.cameraId || 'site',
    cameraLabel: '',
    location: '',
    cameraOnline: onlineCount != null && onlineCount > 0,
    nowRows: summary?.now?.conditionRows,
    freshnessLabel: summary?.measurementHealth?.freshnessLabel || summary?.now?.freshnessLabel,
    ageLabel: summary?.now?.ageLabel,
  });
  const headerFreshness = formatOverviewV2CameraFreshness(siteHealthFacts);
  const countPresentation: 'measured' | 'unavailable' | 'partial' | 'stale' = summary == null
    ? 'unavailable'
    : readState === 'stale'
      ? 'stale'
      : readState === 'unavailable'
        ? 'unavailable'
        : readState === 'partial' || summary.siteTotalsAuthoritative === false
          ? 'partial'
          : 'measured';
  const cameraHealthValue = overviewV2CameraHealthValue({
    onlineCount,
    totalLinkedCameras: onlineCount != null && offlineCount != null ? onlineCount + offlineCount : null,
    availableCameraCount: summary?.measurementHealth?.availableCameraCount,
    totalCameraCount: summary?.measurementHealth?.totalCameraCount,
    cameraViewsLabel: summary?.measurementHealth?.cameraViewsLabel,
  });
  const cameraHealthRows: Array<OverviewV2CameraHealthRow & { rowKey: string }> = areaCameraState.map(({ area, camera, cameraOnline }) => {
    const lastFrameAt = camera?.health?.last_frame || camera?.health_state?.last_frame_at || null;
    return {
      rowKey: area.key,
      ...formatOverviewV2CameraHealthRow(
        buildOverviewV2CameraHealthFacts({
          cameraId: area.cameraId || area.key,
          cameraLabel: areaCameraLabel(area, camera),
          location: area.title,
          cameraOnline: area.cameraId ? cameraOnline : null,
          lastFrameAt,
          nowRows: summary?.now?.conditionRows,
          freshnessLabel: summary?.measurementHealth?.freshnessLabel || summary?.now?.freshnessLabel,
          ageLabel: summary?.now?.ageLabel,
        }),
        siteTimeZone,
      ),
    };
  });

  return (
    <Box data-testid="overview-v2-area-shell" data-binding={binding} data-read-state={countPresentation === 'measured' ? 'authoritative' : countPresentation} sx={{ minWidth: 0, color: colors.ink, fontSize: 12.5, lineHeight: 1.42, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', mb: '9px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Box>
            <Box component="h1" sx={{ m: 0, fontSize: 20, fontWeight: 680, letterSpacing: '-.4px' }}>Safety overview</Box>
            <Box data-testid="overview-v2-site-scope" data-site-id={siteId} data-place-kind="site" sx={{ fontSize: 11, color: colors.ink2, mt: '1px' }}>
              Site · {siteId}
            </Box>
          </Box>
          {/* Temporary booth shortcut: always-clickable Showcase tab on Overview. */}
          <OverviewLink
            href={SHOWCASE_BOOTH_SHORTCUT_HREF}
            testId="overview-showcase-booth-shortcut"
            sx={{
              fontSize: 11,
              fontWeight: 750,
              border: `1px solid ${colors.line}`,
              borderRadius: '6px',
              px: '10px',
              py: '5px',
              bgcolor: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            Showcase
          </OverviewLink>
        </Box>
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${colors.line}`, borderRadius: '7px', p: '4px 9px', fontSize: 9.5, color: colors.ink2 }}>
          Measured through
          <Box component="b" data-testid="overview-v2-measured-through" sx={{ display: 'block', color: colors.ink, fontWeight: 600, fontSize: 11 }}>{formatMeasuredThrough(resolveOverviewV2FinalizedConditionMeasuredThrough(summary?.now.conditionRows, siteTimeZone), siteTimeZone)}</Box>
        </Box>
      </Box>
      <Summary
        areas={areas}
        summary={summary}
        onlineCount={onlineCount}
        offlineCount={offlineCount}
        attentionItems={attentionItems}
        cameraHealthValue={cameraHealthValue}
        countPresentation={countPresentation}
      />
      <AttentionBanner items={personOwnedAttention} onNavigate={onNavigate} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', m: '3px 0 7px' }}>
        <Box>
          <Box component="h2" sx={{ m: 0, fontSize: 14, fontWeight: 700, letterSpacing: '-.2px' }}>Monitored areas</Box>
          <Box component="p" sx={{ m: '1px 0 0', fontSize: 10.5, color: colors.ink3 }}>
            Area → Camera → Safety condition → Measurement → Control. Zone is camera-relative geometry on a condition. Each camera keeps its own conditions and measurement; results are not combined across cameras.
          </Box>
        </Box>
        <OverviewLink href={`/acceptance/site-view${siteId ? `?site=${encodeURIComponent(siteId)}` : ''}`} onNavigate={onNavigate} sx={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>View all areas →</OverviewLink>
      </Box>
      {(summary?.failedReads || []).length ? (
        <Box data-testid="overview-v2-failed-condition" sx={{ bgcolor: '#fff6ea', border: `1px solid #f0d2a8`, borderRadius: '9px', p: '10px 12px', mb: '10px' }}>
          <Box component="b" sx={{ display: 'block', fontSize: 12.5 }}>Unavailable</Box>
          <Box sx={{ fontSize: 10.5, color: colors.ink2, mt: '2px' }}>
            {summary?.failedReads?.length} safety condition {summary?.failedReads?.length === 1 ? 'read' : 'reads'} could not be retrieved. Those conditions are not counted as zero.
          </Box>
          <Box component="details" sx={{ mt: '4px' }}>
            <Box component="summary" sx={{ cursor: 'pointer', fontSize: 10.5, color: colors.blue }}>Read diagnostic</Box>
            {(summary?.failedReads || []).map((failure) => (
              <Box key={failure.conditionId} sx={{ fontSize: 10, color: colors.ink2, mt: '2px' }}>
                {failure.code === 'http_error' ? `HTTP ${failure.httpStatus ?? 'no response'}` : failure.code}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
      {areas.length ? (
        <Box data-testid="overview-v2-zone-matrix">
          <Box data-testid="overview-v2-policy-headings" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: zoneMatrixColumns, bgcolor: '#f8fafc', border: `1px solid ${colors.line}`, borderBottom: 0, borderTopLeftRadius: '9px', borderTopRightRadius: '9px', fontSize: 8.5, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: colors.ink3, '& > div': { p: '7px 10px', borderRight: `1px solid ${colors.line}` }, '& > div:last-child': { borderRight: 0 } }}>
            <div>Camera / Condition</div>
            <div>
              Current activity
              <Box sx={{ mt: '2px', fontSize: 8, fontWeight: 650, letterSpacing: '.2px', textTransform: 'none', color: colors.ink2 }} data-testid="overview-policy-now-clock">{OVERVIEW_V2_CURRENT_WINDOW_HEADING}</Box>
              <Box sx={{ mt: '2px', fontSize: 8, fontWeight: 650, letterSpacing: '.2px', textTransform: 'none', color: colors.ink3 }}>{OVERVIEW_V2_PREVIOUS_WINDOW_HEADING}</Box>
            </div>
            <div>
              Recurring
              <Box sx={{ mt: '2px', fontSize: 8, fontWeight: 650, letterSpacing: '.2px', textTransform: 'none', color: colors.ink2 }}>History & patterns</Box>
              <Box data-testid="overview-v2-recurring-window" sx={{ display: 'flex', gap: '4px', mt: '6px', textTransform: 'none', letterSpacing: 0 }}>
                {OVERVIEW_V2_RECURRING_WINDOWS.map((choice) => {
                  const selected = (selectedOverTimeWindowId || summary?.overTime.selectedWindowId || 'rolling_24h') === choice.id;
                  return (
                    <Box
                      key={choice.id}
                      component="button"
                      type="button"
                      data-testid={`overview-v2-recurring-window-${choice.label.toLowerCase()}`}
                      onClick={() => onOverTimeWindowChange?.(choice.id)}
                      sx={{
                        fontSize: 9,
                        fontWeight: 750,
                        border: `1px solid ${selected ? colors.blue : colors.line}`,
                        bgcolor: selected ? colors.blueSoft : '#fff',
                        color: selected ? colors.blue : colors.ink2,
                        borderRadius: '4px',
                        px: '6px',
                        py: '2px',
                        cursor: onOverTimeWindowChange ? 'pointer' : 'default',
                      }}
                    >
                      {choice.label}
                    </Box>
                  );
                })}
              </Box>
            </div>
            <div>
              Control
              <Box sx={{ mt: '2px', fontSize: 8, fontWeight: 650, letterSpacing: '.2px', textTransform: 'none', color: colors.ink2 }}>Since corrective action</Box>
            </div>
            <div>
              Action
              <Box sx={{ mt: '2px', fontSize: 8, fontWeight: 650, letterSpacing: '.2px', textTransform: 'none', color: colors.ink2 }}>Next step</Box>
            </div>
          </Box>
          {spatialGroups.map((group) => {
            const expanded = Boolean(resolvedExpansion[group.key]);
            const panelId = `overview-v2-area-panel-${group.key}`;
            return (
            <Box
              key={group.key}
              data-testid="overview-v2-area-group"
              data-area-id={group.areaId || overviewV2AreaGroupKey(group.areaId)}
              data-area-label={group.areaLabel}
              data-expanded={expanded ? 'true' : 'false'}
              data-place-kind="area"
            >
              <AreaGroupHeader
                group={group}
                expanded={expanded}
                onToggle={() => toggleArea(group.key)}
              />
              {expanded ? (
              <Box id={panelId}>
              {group.cameras.map((area) => {
                const row = areaCameraState.find((item) => item.area.key === area.key);
                if (!row) return null;
                const { camera, cameraOnline } = row;
                const thumbnail = resolveOverviewV2SnapshotUrl(
                  cameraEvidence.thumbnails,
                  area,
                  camera,
                  allowFixture ? undefined : evidenceThumbs,
                );
                const evidencePending = !allowFixture && area.policies.some((policy) => {
                  const status = evidenceThumbs[policy.conditionId]?.status;
                  return status === 'idle' || status === 'loading';
                });
                return (
                  <AreaCard
                    key={area.key}
                    area={area}
                    summary={summary}
                    camera={camera}
                    cameraOnline={cameraOnline}
                    thumbnail={thumbnail}
                    snapshotPending={!thumbnail && (cameraEvidence.listLoading || cameraEvidence.thumbnailsLoading || evidencePending)}
                    overlays={overlaysByArea[area.key] || []}
                    fixtureProvenance={Boolean(activeFixtureZones)}
                    siteId={siteId}
                    workflowIdentity={workflowIdentity}
                    workflowIdentities={workflowIdentities}
                    attentionWorkflowKeys={attentionWorkflowKeys}
                    onNavigate={onNavigate}
                    cameraMeasurementOverride={
                      cameraMeasurementOverrides?.[area.key]
                      || (area.cameraId ? cameraMeasurementOverrides?.[area.cameraId] : undefined)
                      || null
                    }
                    now={now}
                    cameraMeasurementEnabled={
                      !allowFixture
                      || Boolean(
                        cameraMeasurementOverrides?.[area.key]
                        || (area.cameraId && cameraMeasurementOverrides?.[area.cameraId]),
                      )
                    }
                    selectedOverTimeWindowId={selectedOverTimeWindowId}
                  />
                );
              })}
              </Box>
              ) : null}
            </Box>
            );
          })}
        </Box>
      ) : (
        <Box data-testid="overview-v2-empty-areas" sx={{ bgcolor: '#fff', border: `1px solid ${colors.line}`, borderRadius: '9px', p: '12px', mb: '10px' }}>
          {countPresentation === 'measured' ? (
            <>
              <Box component="b" sx={{ display: 'block', fontSize: 12.5 }}>0 monitored areas</Box>
              <Box sx={{ fontSize: 10, color: colors.ink2, mt: '2px' }}>The read succeeded and no safety conditions are configured for this site.</Box>
            </>
          ) : (
            <>
              <Box component="b" sx={{ display: 'block', fontSize: 12.5 }}>{countPresentation === 'partial' ? 'Partial' : 'Unavailable'}</Box>
              <Box sx={{ fontSize: 10, color: colors.ink2, mt: '2px' }}>Condition-state could not provide area rows. Missing readings are not shown as zero.</Box>
            </>
          )}
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '10px', mt: '4px' }}>
        <AttentionQueue
          items={personOwnedAttention}
          bannerItem={personOwnedAttention.find((item) => item.targetPath) || personOwnedAttention[0] || null}
          onNavigate={onNavigate}
        />
        <MeasurementHealthPanel
          summary={summary}
          rows={cameraHealthRows}
          cameraHealthValue={cameraHealthValue}
          freshnessLabel={headerFreshness}
        />
      </Box>
    </Box>
  );
};
